import React, {lazy, Suspense, useEffect, useState} from 'react';
import {createBrowserRouter, createRoutesFromElements, Navigate, Outlet, Route, RouterProvider, useParams} from "react-router-dom";
import HomePage from "./pages/home/HomePage";
import NoPagePage from "./pages/no-page/NoPagePage";
import LogoutPage from "./pages/logout/LogoutPage";
import LoginPage from "./pages/login/LoginPage";
import SignupPage from "./pages/signup/SignupPage";
import LandingPage from "./pages/landing/LandingPage";
import PrivacyPolicyPage from "./pages/privacy-policy/PrivacyPolicyPage";
import TermsOfServicePage from "./pages/terms-of-service/TermsOfServicePage";
import SupportPage from "./pages/support/SupportPage";
import PhoneVerifyPage from "./pages/phone-verify/PhoneVerifyPage";
import MeasureTreePage from "./pages/measure-tree/MeasureTreePage";
import './App.css';
import {blue, green} from "@mui/material/colors";
import {createTheme, CssBaseline, ThemeProvider} from "@mui/material";
import AuthGuard from "./components/AuthGuard";
import { SnackbarProvider } from 'notistack';
import { datadogRum } from '@datadog/browser-rum';
import { datadogLogs } from '@datadog/browser-logs';
import Layout from "./components/Layout";
import {ToolbarProvider} from "./components/ToolbarContext";
import EstimateItemizedPage from "./pages/estimate-itemized/EstimateItemizedPage";
import EstimateListPage from "./pages/estimates/EstimateListPage";
import ClientListPage from "./pages/client/ClientListPage";
import AppointmentListPage from "./pages/appointment/AppointmentListPage";
import WorkOrderListPage from "./pages/work-order/WorkOrderListPage";
import InvoiceListPage from "./pages/invoice/InvoiceListPage";
import InvoiceDueDatesPage from "./pages/invoice/InvoiceDueDatesPage";
import EquipmentListPage from "./pages/equipment/EquipmentListPage";
import {GoogleOAuthProvider} from "@react-oauth/google";
import {SettingsPage} from "./pages/settings/SettingsPage";
import TeamAccessPage from "./pages/settings/TeamAccessPage";
import JobPricingPage from "./pages/settings/JobPricingPage";
import PlanPage from "./pages/settings/PlanPage";
import {AccountPage} from "./pages/account/AccountPage";
import {PermissionProvider} from "./shared/permissions/PermissionContext";
import LegalAcceptanceGate from "./components/legal/LegalAcceptanceGate";
import PermissionGuard from "./shared/permissions/PermissionGuard";
import {PermissionCode} from "./shared/permissions/permissions";
import {LocationPermissionsProvider} from "./components/LocationPermissionsContext";
import FollowUpsPage from "./pages/estimates/FollowUpsPage";

import {RealTimeProvider} from "./shared/providers/real-time.provider";
import {UserSettingsProvider} from "./components/UserSettingsContext";
import SelfContainedAuthPage from './pages/self-contained-auth/SelfContainedAuthPage';
import OperationMethodListPage from './pages/equipment/OperationMethodListPage';
import OperationMethodDetailPage from './pages/equipment/OperationMethodDetailPage';
import CompleteJobPage from './pages/job/CompleteJobPage';
import AdminPage, {ChunkBoundary} from './pages/admin/AdminPage';
import WorkOrderItemizedPage from './pages/work-order/WorkOrderItemizedPage';
import CustomerRecordPage from './pages/customer/CustomerRecordPage';
import ShortLinkRedirectPage from './pages/short-link/ShortLinkRedirectPage';

const LibraryPreparationPage = lazy(() => import('./pages/admin/library/LibraryPreparationPage'));
/** One page per estimate: moving on to the next estimate starts clean. */
const KeyedLibraryPreparation = () => <LibraryPreparationPage key={useParams().estimateId}/>;

function App() {
  useEffect(() => {
    document.title = 'SavaQuan';

    if(process.env.REACT_APP_DD_ENV !== 'local') {
      if (!(window as any)?.DD_RUM?.getInternalContext()?.session_id) {
        datadogRum.init({
          applicationId: 'f848c0cb-d037-453a-8c38-c716f69c8fb0',
          clientToken: 'pubd3ad6f80ded8b792a804b56b81f8d45c',
          site: 'us5.datadoghq.com',
          service: 'accusite-ui',
          env: process.env.REACT_APP_DD_ENV,
          version: process.env.REACT_APP_VERSION,
          sessionSampleRate: 100,
          sessionReplaySampleRate: 100,
          trackUserInteractions: true,
          trackResources: true,
          trackLongTasks: true,
          defaultPrivacyLevel: 'allow',
        });
      }

      if (!(window as any)?.DD_LOGS?.getInternalContext()?.session_id) {
        datadogLogs.init({
          clientToken: 'pubd3ad6f80ded8b792a804b56b81f8d45c',
          site: 'us5.datadoghq.com',
          service: 'accusite-ui',
          env: process.env.REACT_APP_DD_ENV,
          version: process.env.REACT_APP_VERSION,
          forwardErrorsToLogs: true,
          sessionSampleRate: 100
        });
      }
    }
  }, []);
  const theme = createTheme({
    palette: {
      // SQ-538: the iOS app's accent and button green (GreenDeep), so
      // contained buttons are white on it.
      primary: {
        main: green[800],
      },
      secondary: {
        main: blue[500],
      },
      mode: 'light',
    },
  });

  const AuthenticatedUserLayout = () => {
    return (
      <PermissionProvider>
        <LegalAcceptanceGate>
          <Layout>
            <Outlet />
          </Layout>
        </LegalAcceptanceGate>
      </PermissionProvider>
    );
  }

  const guarded = (
    element: React.ReactNode,
    permission?: string,
    anyOf?: string[],
  ) => (
    <AuthGuard>
      <PermissionGuard permission={permission} anyOf={anyOf}>{element}</PermissionGuard>
    </AuthGuard>
  );

  const TreeRedirect = () => {
    const { estimateId, treeId } = useParams();
    return <Navigate to={`/estimates/${estimateId}/itemized/tree/${treeId || ''}`} replace />;
  }

  // A stable data router provides the supported navigation blocker, including Back.
  // Route definitions and permission guards are unchanged.
  const [router] = useState(() => createBrowserRouter(createRoutesFromElements(
            <Route path="/">
              <Route path="" element={<LandingPage/>} />
              <Route path="privacy-policy" element={<PrivacyPolicyPage/>} />
              <Route path="terms-of-service" element={<TermsOfServicePage/>} />
              <Route path="support" element={<SupportPage/>} />
              {/* Phone sign-in's reCAPTCHA step for the iPhone app (no sign-in needed). */}
              <Route path="auth/phone-verify" element={<PhoneVerifyPage/>} />
              {/* Take Measured Picture's address when no phone app took the link (no sign-in needed). */}
              <Route path="measure-tree" element={<MeasureTreePage/>} />
              <Route path="login" element={<LoginPage/>} />
              <Route path="signup" element={<SignupPage/>} />
              <Route path="logout" element={<LogoutPage/>} />

              <Route element={<AuthenticatedUserLayout/>}>
                <Route path="home" element={<AuthGuard><HomePage/></AuthGuard>} />
                <Route path="admin">
                  <Route index element={<AuthGuard><AdminPage/></AuthGuard>} />
                  <Route path="ai-ops" element={<AuthGuard><AdminPage/></AuthGuard>} />
                  <Route path="library" element={<AuthGuard><AdminPage/></AuthGuard>} />
                  <Route path="accounts" element={<AuthGuard><AdminPage/></AuthGuard>} />
                  <Route path="billing" element={<AuthGuard><AdminPage/></AuthGuard>} />
                  <Route path="time-on-site" element={<AuthGuard><AdminPage/></AuthGuard>} />
                  <Route path="ai-pricing" element={<AuthGuard><AdminPage/></AuthGuard>} />
                  <Route path="library/estimates/:estimateId" element={<AuthGuard><ChunkBoundary><Suspense fallback={null}><KeyedLibraryPreparation/></Suspense></ChunkBoundary></AuthGuard>} />
                </Route>
                {/*<Route path="signalr-test" element={<AuthGuard><SignalRTestPage/></AuthGuard>} />*/}
                {/*<Route path="maps-test" element={<AuthGuard><GoogleEarthPage/></AuthGuard>} />*/}
                {/*<Route path="device-imu-data" element={<AuthGuard><DeviceIMUData/></AuthGuard>} />*/}
                {/* A record opens as its own page over its list (SQ-574 rule 2): <list>/12, <list>/new. */}
                <Route path="appointments/*" element={guarded(<AppointmentListPage/>, PermissionCode.AppointmentsViewList)} />
                {/* The old map page: now the Appointments page with its map open. */}
                <Route path="appointments/map" element={<Navigate to="/appointments" replace state={{mapOpen: true}}/>} />
                <Route path="clients/*" element={guarded(<ClientListPage/>, PermissionCode.ClientsViewList)} />
                {/* SQ-596: an estimate is its own page over the list (/estimates/12); the itemized pages stay at their addresses. */}
                <Route path="estimates/:estimateId/itemized">
                  <Route path="tree/:treeId?" element={guarded(<EstimateItemizedPage/>, PermissionCode.EstimatesViewList)} />
                  <Route path="" element={guarded(<EstimateItemizedPage/>, PermissionCode.EstimatesViewList)} />
                </Route>
                <Route path="estimates/:estimateId/tree/:treeId?" element={<TreeRedirect />} />
                <Route path="estimates/automatic-follow-ups" element={guarded(<FollowUpsPage/>, PermissionCode.EstimatesManageFollowUps)}/>
                <Route path="estimates/*" element={guarded(<EstimateListPage/>, PermissionCode.EstimatesViewList)}/>
                <Route path="jobs/*" element={guarded(<WorkOrderListPage/>, undefined, [PermissionCode.JobsViewAll, PermissionCode.JobsViewAssigned])}/>
                <Route path="work-orders">
                  <Route path=":workOrderId">
                    <Route path="itemized">
                      <Route path="tree/:treeId?" element={guarded(<WorkOrderItemizedPage/>, undefined, [PermissionCode.JobsViewAll, PermissionCode.JobsViewAssigned])} />
                      <Route path="" element={guarded(<WorkOrderItemizedPage/>, undefined, [PermissionCode.JobsViewAll, PermissionCode.JobsViewAssigned])} />
                    </Route>

                    <Route path="complete-job" element={guarded(<CompleteJobPage/>, PermissionCode.JobsComplete)} />
                    <Route path="" element={guarded(<WorkOrderItemizedPage/>, undefined, [PermissionCode.JobsViewAll, PermissionCode.JobsViewAssigned])} />
                  </Route>
                  {/* SQ-597: the Jobs list and each job's page live at /jobs. */}
                  <Route path="" element={<Navigate to="/jobs" replace/>}/>
                </Route>
                <Route path="equipment/*" element={guarded(<EquipmentListPage/>, PermissionCode.EquipmentViewList)} />
                <Route path="equipment/operation-methods">
                  <Route path="" element={<AuthGuard><OperationMethodListPage/></AuthGuard>} />
                  <Route path=":operationMethodId" element={<AuthGuard><OperationMethodDetailPage/></AuthGuard>} />
                </Route>
                {/* SQ-687: a static path, so it outranks invoices/* (an invoice's page is /invoices/<number>). */}
                <Route path="invoices/due-dates" element={guarded(<InvoiceDueDatesPage/>, PermissionCode.InvoicesManageDueDates)}/>
                {/* SQ-598: the Invoices list and each invoice's page live at /invoices. */}
                <Route path="invoices/*" element={guarded(<InvoiceListPage/>, PermissionCode.InvoicesViewList)} />

                <Route path="settings" element={<AuthGuard><SettingsPage/></AuthGuard>} />
                <Route path="settings/access/*" element={guarded(<TeamAccessPage/>, PermissionCode.RbacManage)} />
                <Route path="settings/pricing" element={guarded(<JobPricingPage/>, PermissionCode.SettingsPricing)} />
                <Route path="settings/plan" element={guarded(<PlanPage/>, PermissionCode.SettingsBilling)} />
                {/* SQ-652: the person's own Account page, opened from who is signed in at the menu's foot. */}
                <Route path="account" element={<AuthGuard><AccountPage/></AuthGuard>} />
                <Route path="settings/account" element={<Navigate to="/account" replace/>} />
              </Route>

              <Route path="self-contained-auth/*" element={<SelfContainedAuthPage/>} />

              {/* SQ-337: the customer's links, on the record's own page in its customer form. */}
              <Route path="external">
                <Route path="invoices">
                  <Route path=":invoiceId" element={<CustomerRecordPage key="invoice" kind="invoice"/>}/>
                </Route>
                <Route path="estimates">
                  <Route path=":estimateId" element={<CustomerRecordPage key="estimate" kind="estimate"/>}/>
                </Route>
                <Route path="receipts">
                  <Route path=":invoiceId" element={<CustomerRecordPage key="receipt" kind="receipt"/>}/>
                </Route>
              </Route>

              {/* Opaque customer short links, e.g. /a7K9P284 → /external/estimates/... */}
              <Route path=":shortCode" element={<ShortLinkRedirectPage/>} />

              <Route path="*" element={<NoPagePage/>} />
            </Route>
  )));

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline/>
      <SnackbarProvider/>
      <RealTimeProvider>
      <ToolbarProvider>
      <LocationPermissionsProvider>
      <GoogleOAuthProvider clientId="191352312973-5vhn9rudf8l1q4rci7e8jpoiaau8bktc.apps.googleusercontent.com">
      <UserSettingsProvider>
        <RouterProvider router={router}/>
      </UserSettingsProvider>
      </GoogleOAuthProvider>
      </LocationPermissionsProvider>
      </ToolbarProvider>
      </RealTimeProvider>
    </ThemeProvider>
  );
}

export default App;
